import {PricingNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNode, GraphNodeDefinition} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

type ReturnsOf<N> = N extends GraphNode<infer R, PricingContext> ? R : never

export type PricingListParameters<NodeItem extends PricingNode> = {list: NodeItem[]}

export abstract class PricingListNode<NodeItem extends PricingNode> extends PricingNode<ReturnsOf<NodeItem>[], PricingListParameters<NodeItem>> {
    override run = async (evaluator: Evaluator<PricingContext>) => {
        return Promise.all(this.parameters.list.map((node) => evaluator.get(node))) as Promise<ReturnsOf<NodeItem>[]>
    }

    addEntry(node: NodeItem) {
        this.replaceParameters({list: [...this.parameters.list, node]})
    }

    removeEntry(node: NodeItem) {
        const index = this.parameters.list.indexOf(node)
        if (index === -1) return
        const newList = [...this.parameters.list]
        newList.splice(index, 1)
        this.replaceParameters({list: newList})
    }

    clear() {
        this.replaceParameters({list: []})
    }
}

export function pricingListDefinition<NodeItem extends PricingNode>(
    nodeClass: string,
    itemClass: abstract new (...args: any[]) => NodeItem,
    itemReturnsSchema: z.ZodMiniType<ReturnsOf<NodeItem>>,
): GraphNodeDefinition<ReturnsOf<NodeItem>[], PricingListParameters<NodeItem>> {
    return {
        nodeClass,
        validation: {
            parameters: z.object({list: z.array(z.instanceof(itemClass) as z.ZodMiniType<NodeItem>)}) as z.ZodMiniType<
                PricingListParameters<NodeItem>,
                PricingListParameters<NodeItem>
            >,
            returns: z.array(itemReturnsSchema),
        },
    }
}
